import React, { useState } from 'react';
import {Dropdown, DropdownItem, DropdownMenu, DropdownToggle} from 'reactstrap';
import Badge from "@material-ui/core/Badge";
import NotificationsActive from "@material-ui/icons/NotificationsActive";
import Types from "../../constants/Types";
import API from "@beardeddevops/react.api";
import {getEngineerPkey} from "../Parts/Banners/helpdeskCallQueue";

const DD_INFO_TYPE = 5;
const HELPDESK_CALL_FE_TYPE = 9;
const FE_METRIC_THRESHOLD_TYPE = 10;
const MISSING_FINANCIAL_TYPE = 11;

const getNotificationType = (notification) => Number(notification?.Type ?? notification?.type);

const getNotificationData = (notification) => notification?.Data ?? notification?.data ?? null;

const parseNotificationData = (data) => {
	if (data == null || data === '' || data === '{}') {
		return null;
	}
	if (typeof data === 'object') {
		return data;
	}
	try {
		return JSON.parse(data);
	} catch (e) {
		return null;
	}
};

export const formatMissingFinancialLabel = (notification) => {
	const payload = parseNotificationData(notification?.Data ?? notification?.data);
	const engineerName = String(payload?.data?.engineer_name || '').trim() || 'Field Engineer';
	const customerName = String(payload?.data?.customer_name || payload?.data?.ticket_customer || '').trim();
	if (customerName) {
		return `Missing Financial Today - ${engineerName} for ${customerName}`;
	}
	const serverMessage = notification?.NotificationTypes?.Message
		?? notification?.notificationTypes?.Message;
	if (typeof serverMessage === 'string' && serverMessage.indexOf('Missing Financial Today - ') === 0) {
		return serverMessage;
	}
	return `Missing Financial Today - ${engineerName} for Customer`;
};

const FE_METRIC_META = {
	low_dollar_average: {label: 'Low Dollar Average', unit: '$'},
	av_conversion: {label: 'AV Conversion', unit: '%'},
	low_reviews: {label: 'Reviews', unit: '%'},
	high_four_point_waived: {label: 'Four-Point Waived', unit: '%'},
	low_cards: {label: 'Cards Left', unit: '%'},
};

export const formatFeMetricLabel = (notification) => {
	const payload = parseNotificationData(notification?.Data ?? notification?.data);
	const data = payload?.data && typeof payload.data === 'object' ? payload.data : {};
	const engineerName = String(data.engineer_name || '').trim() || 'Field Engineer';
	const meta = FE_METRIC_META[data.category] || {label: 'FE metric', unit: ''};
	const intervalKey = data.interval_key;
	const interval = intervalKey === 'week' || ['1d', '7d', '30d'].includes(intervalKey) ? intervalKey : '';
	const title = [meta.label, interval].filter(Boolean).join(' ');
	const value = data.value;
	const threshold = data.threshold ?? data.threshold_amount;
	if (value != null && value !== '' && threshold != null && threshold !== '') {
		const shown = meta.unit === '$'
			? `$${Math.round(Number(value))} vs $${Math.round(Number(threshold))}`
			: `${Number(value).toFixed(1)}% vs ${Math.round(Number(threshold))}%`;
		return `${title} - ${engineerName} (${shown})`;
	}
	return `${title} - ${engineerName}`;
};

const getNotificationLabel = (notification) => {
	const notificationType = getNotificationType(notification);
	const notificationData = getNotificationData(notification);
	if ((notificationType === DD_INFO_TYPE || notificationType === 6 || notificationType === HELPDESK_CALL_FE_TYPE || notificationType === FE_METRIC_THRESHOLD_TYPE || notificationType === MISSING_FINANCIAL_TYPE) && notificationData) {
		try {
			const payload = parseNotificationData(notificationData);
			const userName = payload?.data?.user_name;
			const engineerName = payload?.data?.engineer_name;
			if (notificationType === HELPDESK_CALL_FE_TYPE && engineerName) {
				return `Call ${engineerName.trim()}`;
			}
			if (notificationType === FE_METRIC_THRESHOLD_TYPE) {
				return formatFeMetricLabel(notification);
			}
			if (notificationType === MISSING_FINANCIAL_TYPE) {
				return formatMissingFinancialLabel(notification);
			}
			if (userName) {
				return notificationType === DD_INFO_TYPE
					? `DD Info Update for ${userName}`
					: `Schedule Adjustment for ${userName}`;
			}
		} catch (e) {
			// fall through to server-provided message
		}
	}
	return notification?.NotificationTypes?.Message
		?? notification?.notificationTypes?.Message
		?? 'You have a pending notification.';
};

const isWorkflowLockedNotificationType = (notificationType) => (
	notificationType === DD_INFO_TYPE
	|| notificationType === HELPDESK_CALL_FE_TYPE
	|| notificationType === MISSING_FINANCIAL_TYPE
);

const getNotificationPkey = (notification) => notification?.Pkey ?? notification?.pkey ?? null;

export const HELPDESK_CONTACTED_HIGHLIGHT_EVENT = 'helpdesk-contacted-highlight';
export const HELPDESK_CONTACTED_UPDATED_EVENT = 'helpdesk-contacted-updated';

export const notifyHelpdeskContactedUpdated = (engineerPkey) => {
	window.dispatchEvent(new CustomEvent(HELPDESK_CONTACTED_UPDATED_EVENT, {
		detail: {pkey: engineerPkey != null ? Number(engineerPkey) : null},
	}));
};

export const goToHelpdeskContacted = (history, engineerPkey, date = null) => {
	const pkey = engineerPkey != null && engineerPkey !== '' ? Number(engineerPkey) : null;
	const pathname = (history?.location?.pathname || '').replace(/\/$/, '');
	const alreadyOnPage = pathname === '/helpdesks/contacted';
	const dateParam = (typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date)) ? date : null;
	const hash = pkey != null && !Number.isNaN(pkey) ? `#?pkey=${pkey}` : undefined;
	const search = dateParam ? `?date=${dateParam}` : undefined;

	if (pkey != null && !Number.isNaN(pkey)) {
		window.dispatchEvent(new CustomEvent(HELPDESK_CONTACTED_HIGHLIGHT_EVENT, {
			detail: {pkey, date: dateParam},
		}));
	}

	if (alreadyOnPage) {
		const currentDate = new URLSearchParams(history?.location?.search || '').get('date');
		const currentHash = history?.location?.hash || '';
		const dateChanged = dateParam && dateParam !== currentDate;
		const hashChanged = hash && currentHash !== hash;
		if (dateChanged || hashChanged) {
			history.replace({
				pathname: '/helpdesks/contacted',
				search: search ?? history?.location?.search,
				hash,
			});
		}
		return;
	}

	history.push({
		pathname: '/helpdesks/contacted',
		search,
		hash,
	});
};

export const navigateForNotification = (notificationData, history) => {
	if (!notificationData?.url) {
		history.push('/');
		return;
	}

	if (notificationData.url.includes('https')) {
		window.open(notificationData.url, '_blank');
		return;
	}

	const scheduleUrl = notificationData.url === 'schedule_adjust' || notificationData.url === 'timeoff';
	if (scheduleUrl) {
		if (notificationData.pkey) {
			history.push({ pathname: `/schedule_adjust/${notificationData.pkey}` });
		} else {
			history.push({ pathname: '/schedule_adjust' });
		}
		return;
	}

	const changedUserPkey = notificationData.data?.user;
	if (notificationData.url === 'users' && changedUserPkey) {
		history.push({ pathname: `/users/${changedUserPkey}` });
		return;
	}

	const isHelpdeskCall = notificationData.url === 'helpdesks' || notificationData.url === 'helpdesks/contacted';
	if (isHelpdeskCall) {
		const engineerPkey = notificationData.pkey ?? notificationData.data?.engineer_pkey;
		goToHelpdeskContacted(history, engineerPkey);
		return;
	}

	if (notificationData.url === 'helpdesks/fe_performance') {
		const fe = notificationData.pkey ?? notificationData.data?.engineer_pkey;
		const interval = notificationData.data?.interval_key;
		const params = new URLSearchParams();
		if (fe) {
			params.set('fe', String(fe));
		}
		const category = notificationData.data?.category;
		if (category) {
			params.set('category', String(category));
		}
		if (interval === 'week') {
			params.set('interval', 'week');
		} else if (['1d', '7d', '30d'].includes(interval)) {
			params.set('interval', interval);
		}
		history.push({
			pathname: '/helpdesks/fe_performance',
			search: params.toString() ? `?${params}` : undefined,
		});
		return;
	}

	if (notificationData.url === 'helpdesks/missing_financial') {
		const pkey = notificationData.pkey ?? notificationData.data?.workflow_pkey;
		history.push({
			pathname: '/helpdesks/missing_financial',
			hash: pkey != null && pkey !== '' ? `#?pkey=${pkey}` : undefined,
		});
		return;
	}

	if (notificationData.highlight) {
		const tab = notificationData.tab ?? '';
		const pkey = notificationData.pkey;
		let hash;
		if (tab && pkey != null && pkey !== '') {
			hash = `#${tab}?pkey=${pkey}`;
		} else if (pkey != null && pkey !== '') {
			hash = `#?pkey=${pkey}`;
		} else if (tab) {
			hash = `#${tab}`;
		}
		history.push({
			pathname: '/' + notificationData.url,
			hash,
		});
		return;
	}

	history.push({ pathname: '/' + notificationData.url });
};

const Notifications = (props) => {
	const [dropdownOpen, setDropdownOpen] = useState(false);

	const notifications = Array.isArray(props.notifications) ? props.notifications : [];
	const toggle = () => notifications.length > 0 ? setDropdownOpen(prevState => !prevState) : null;

	const visibleNotifications = notifications.filter((item) => {
		if (getNotificationType(item) !== HELPDESK_CALL_FE_TYPE) {
			return true;
		}
		const engineerPkey = getEngineerPkey(item);
		return engineerPkey != null && !Number.isNaN(engineerPkey);
	});
	const notificationAmount = visibleNotifications.length;

	const handleNotificationClick = async (notification) => {
		setDropdownOpen(false);

		try {
			const rawNotificationData = getNotificationData(notification);
			if (rawNotificationData !== null && rawNotificationData !== '' && rawNotificationData !== '{}') {
				const notificationData = parseNotificationData(rawNotificationData);
				if (notificationData) {
					navigateForNotification(notificationData, props.history);
				}
			} else {
				props.history.push('/');
			}
		} catch (e) {
			console.error('Notification navigation failed:', e);
			API.Toast.error('Unable to open that notification. Please use the menu to navigate instead.', {
				position: API.Toast.POSITION.TOP_CENTER,
				autoClose: 5000,
			});
		}

		const notificationPkey = getNotificationPkey(notification);
		const notificationType = getNotificationType(notification);
		// Type 5 (DD info) is only cleared when Financial/Admin mark it reviewed.
		// Type 9 (call FE) and type 11 (Today Missing) are only cleared by their workflows.
		if (notificationPkey != null && !isWorkflowLockedNotificationType(notificationType)) {
			try {
				const notifications_model = new API.BuildClass(Types.Notifications._name, API.RequestType.DELETE);
				notifications_model.object.pkey = notificationPkey;
				await notifications_model.submit(null, 'DELETE');
			} catch (e) {
				console.error('Notification dismiss failed:', e);
			}
		}

		setTimeout(() => {
			props.triggerSessionCheck();
		}, 750);
	};

	return (
		<Dropdown
			isOpen={dropdownOpen}
			toggle={toggle}
			className="d-inline-block"
			style={
				{
					paddingLeft: "20px",
					paddingRight: "10px",
					border: "1px solid transparent"
				}
			}
		>
			<DropdownToggle
				tag="span"
				data-toggle="dropdown"
				aria-expanded={dropdownOpen}
			>
				{<Badge badgeContent={notificationAmount} color={visibleNotifications.filter((item) => item.Type == 3).length > 0 ? "primary" : "error"}><NotificationsActive/></Badge>}
			</DropdownToggle>
			<DropdownMenu className="notifications-dropdown-menu">
				{visibleNotifications.map((notification, index) => (
					<DropdownItem
						key={getNotificationPkey(notification) ?? index}
						style={{color: "black"}}
						onClick={() => handleNotificationClick(notification)}
					>
						{notification?.amount ? '[' + notification.amount + '] ' : ''} {getNotificationLabel(notification)}
					</DropdownItem>
				))}
			</DropdownMenu>
		</Dropdown>
	);
}

export default Notifications;
